import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useDiscardMta } from '@acorn-portal/shared/src/hooks/use-discard-mta';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { MtaPaymentCollectionOption } from '@acorn-portal/shared/src/services/api';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import CardAndFinancePaymentOptions from './components/card-and-finance-payment-options';
import CardPaymentOption from './components/card-payment-option';
import CardRefundOption from './components/CardRefundOption';
import FinanceRefundOption from './components/FinanceRefundOption';
import { findPaymentOptionHasOneOfTypes } from '@acorn-portal/shared/src/pages/mta/utils';
import { PaymentType } from '@acorn-portal/shared/src/utils/types/payment';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';

const PaymentOptions: FC = () => {
	const [open, toggleModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const midTermAdjustment = useAppSelector((redux) => redux.midTermAdjustment);
	const { track } = useTrack();
	const navigate = useNavigate();
	const { discardLoading } = useDiscardMta();
	const mtaResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);
	const findPaymentOption = (
		array: MtaPaymentCollectionOption[],
		enumValues: PaymentType[],
	): MtaPaymentCollectionOption => {
		return (
			findPaymentOptionHasOneOfTypes(array, enumValues) ??
			({
				unequalInstalmentAmount: 0,
				standardInstalmentAmount: 0,
				totalAmountPayable: 0,
				appliedInstalmentCount: 0,
			} as MtaPaymentCollectionOption)
		);
	};

	useEffect(() => {
		if (!midTermAdjustment.policyReference) {
			navigate(ROUTE_PATHS.home);
		}
		if (midTermAdjustment.mtaPurchased) {
			navigate(ROUTE_PATHS.home);
		}
	}, []);

	useEffect(() => {
		track(`MTA Payment Options Page Viewed`);
	}, []);

	const getPaymentOptions = () => {
		const { isReturnPremium, isCardPaymentAvailable, isFinanceAvailable } = mtaResponse;

		let paymentCollection: MtaPaymentCollectionOption = {
			code: PaymentType.SINGLE_PAYMENT_PAYMENT_CARD_AP,
			unequalInstalmentAmount: 0,
			standardInstalmentAmount: 0,
			totalAmountPayable: 0,
			appliedInstalmentCount: 0,
		};

		if (isReturnPremium) {
			const refundCard =
				mtaResponse.existingCardPaymentMethods.length === 1 ? mtaResponse.existingCardPaymentMethods[0] : undefined;

			if (isFinanceAvailable) {
				paymentCollection = findPaymentOption(mtaResponse.paymentOptions, [
					PaymentType.INSTALMENT_ADJUSTMENT_SPREAD_EVENLY,
					PaymentType.INSTALMENT_ADJUSTMENT_NEXT_INSTALMENT,
				]);
				return <FinanceRefundOption setLoading={setLoading} financeOption={paymentCollection} />;
			}

			if (isCardPaymentAvailable) {
				paymentCollection = findPaymentOption(mtaResponse.paymentOptions, [PaymentType.SINGLE_PAYMENT_PAYMENT_CARD_RP]);
				return (
					<CardRefundOption
						toggleModal={toggleModal}
						refundOption={paymentCollection}
						setLoading={setLoading}
						refundCard={refundCard}
					/>
				);
			}
		} else {
			if (isCardPaymentAvailable && isFinanceAvailable) {
				paymentCollection = findPaymentOption(mtaResponse.paymentOptions, [
					PaymentType.INSTALMENT_ADJUSTMENT_SPREAD_EVENLY,
					PaymentType.INSTALMENT_ADJUSTMENT_NEXT_INSTALMENT,
				]);

				const cardPaymentCollection = findPaymentOption(mtaResponse.paymentOptions, [
					PaymentType.SINGLE_PAYMENT_PAYMENT_CARD_AP,
				]);
				return (
					<CardAndFinancePaymentOptions
						toggleModal={toggleModal}
						setLoading={setLoading}
						financeOption={paymentCollection}
						payInFullOption={cardPaymentCollection}
					/>
				);
			}

			if (isCardPaymentAvailable && !isFinanceAvailable) {
				const cardPaymentCollection = findPaymentOption(mtaResponse.paymentOptions, [
					PaymentType.SINGLE_PAYMENT_PAYMENT_CARD_AP,
				]);
				return <CardPaymentOption toggleModal={toggleModal} payInFullOption={cardPaymentCollection} />;
			}
		}
		navigate(ROUTE_PATHS.error);
		return null;
	};

	return (
		<JourneyCardLayout
			pageTitle={mtaResponse.isReturnPremium ? 'Refund Options' : 'Payment Options'}
			loading={loading || discardLoading}
			loadingMessage={loading ? 'Processing Payment' : undefined}
		>
			{getPaymentOptions()}
			<HelperModal
				open={open}
				onClose={() => {
					toggleModal(false);
				}}
				title='Discard The Changes?'
				description='Are you sure you wish to discard the changes?'
				closeText='No'
				submitText='Yes'
				onSubmit={() => navigate(-1)}
			/>
		</JourneyCardLayout>
	);
};
export default PaymentOptions;
